import React, { useEffect, useMemo, useRef, useState } from "react";
import { Formik, Form as FormikForm, FieldArray } from "formik";
import {
  Button,
  Form,
  Row,
  Col,
  Card,
  Badge,
  Table,
} from "react-bootstrap";
import { toast } from "react-toastify";
import { useNavigate, useParams, Navigate, Link } from "react-router-dom";
import { FaPlus, FaTrash, FaSave } from "react-icons/fa";
import { catalogApi } from "../api/services";
import { getErrorMessage } from "../api/client";
import { useAuth } from "../context/AuthContext";
import FormField from "../components/forms/FormField";
import ProductImageManager from "../components/ProductImageManager";
import {
  CategoryAutocomplete,
  CategoryMultiAutocomplete,
} from "../components/CategoryAutocomplete";
import { adminProductSchema } from "../validation/productAdmin.schema.js";
import {
  flattenCategoryOptions,
  getRootCategoryOptions,
  getSubtreeOptions,
} from "../utils/categories";
import {
  guessColorFromLabel,
  isColorOptionName,
  normalizeOptionValues,
} from "../utils/variants";
import { FormPageSkeleton } from "../components/Skeleton";
import "./AdminProductForm.css";

const isBlobUrl = (url) => typeof url === "string" && url.startsWith("blob:");
const isProductUploadUrl = (url) =>
  typeof url === "string" && url.startsWith("/uploads/products/");

async function materializeProductImages(imageUrls, pendingFiles) {
  const result = [];
  for (const url of imageUrls || []) {
    if (pendingFiles?.has(url)) {
      const file = pendingFiles.get(url);
      const res = await catalogApi.uploadImages([file]);
      const uploaded = res.data.data.urls?.[0];
      if (isBlobUrl(url)) URL.revokeObjectURL(url);
      pendingFiles.delete(url);
      if (uploaded) result.push(uploaded);
    } else if (isBlobUrl(url)) {
      URL.revokeObjectURL(url);
    } else if (url) {
      result.push(url);
    }
  }
  return result;
}

async function deleteRemovedProductImages(previousUrls, nextUrls) {
  const kept = new Set((nextUrls || []).filter(isProductUploadUrl));
  const removals = (previousUrls || []).filter(
    (url) => isProductUploadUrl(url) && !kept.has(url)
  );
  await Promise.all(
    removals.map(async (url) => {
      try {
        await catalogApi.deleteUploadedImage(url);
      } catch {
        // ignore cleanup failures
      }
    })
  );
}

const emptyInitial = {
  name: "",
  sku: "",
  price: "",
  compareAtPrice: "",
  stock: 0,
  category: "",
  categories: [],
  manufacturer: "",
  brand: "",
  description: "",
  shortDescription: "",
  tags: "",
  images: [],
  isActive: true,
  isFeatured: false,
  specifications: [],
  variantOptions: [],
  variants: [],
};

function getOptionEntries(option) {
  if (isColorOptionName(option.name) && option.colorValues?.length) {
    return option.colorValues
      .filter((v) => v.label?.trim())
      .map((v) => ({
        label: v.label.trim(),
        color: v.color || guessColorFromLabel(v.label),
      }));
  }
  return (option.valuesText || "")
    .split(",")
    .map((v) => v.trim())
    .filter(Boolean)
    .map((label) => ({ label, color: "" }));
}

function buildVariantsFromOptions(options, existing = []) {
  const cleaned = options
    .map((o) => ({
      name: o.name?.trim(),
      values: getOptionEntries(o).map((e) => e.label),
    }))
    .filter((o) => o.name && o.values.length);

  if (!cleaned.length) return [];

  const combos = cleaned.reduce(
    (acc, opt) => {
      const next = [];
      acc.forEach((prev) => {
        opt.values.forEach((val) => {
          next.push({ ...prev, [opt.name]: val });
        });
      });
      return next;
    },
    [{}]
  );

  return combos.map((attributes, index) => {
    const match = existing.find((v) => {
      const a = v.attributes || {};
      return Object.keys(attributes).every((k) => a[k] === attributes[k]);
    });
    return {
      sku: match?.sku || "",
      price: match?.price ?? "",
      stock: match?.stock ?? 0,
      image: match?.image || "",
      isDefault: match?.isDefault || index === 0,
      attributes,
    };
  });
}

const AdminProductForm = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const { user, loading: authLoading, isAuthenticated } = useAuth();
  const [categories, setCategories] = useState([]);
  const [loadingProduct, setLoadingProduct] = useState(isEdit);
  const [initialValues, setInitialValues] = useState(emptyInitial);
  const pendingImageFilesRef = useRef(new Map());
  const committedImagesRef = useRef([]);

  useEffect(() => {
    catalogApi
      .adminCategories()
      .then((res) => setCategories(res.data.data.categories || []))
      .catch(() =>
        catalogApi
          .getCategories()
          .then((res) => setCategories(res.data.data.categories || []))
          .catch(() => setCategories([]))
      );
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    setLoadingProduct(true);
    catalogApi
      .getProduct(id)
      .then((res) => {
        const p = res.data.data.product;
        const images = p.images || [];
        committedImagesRef.current = [...images];
        pendingImageFilesRef.current.clear();
        setInitialValues({
          name: p.name || "",
          sku: p.sku || "",
          price: p.price ?? "",
          compareAtPrice: p.compareAtPrice ?? "",
          stock: p.stock ?? 0,
          category: p.category?._id || p.category?.id || p.category || "",
          categories: (p.categories || []).map((c) => c._id || c.id || c),
          manufacturer: p.manufacturer || "",
          brand: p.brand || "",
          description: p.description || "",
          shortDescription: p.shortDescription || "",
          tags: (p.tags || []).join(", "),
          images,
          isActive: p.isActive !== false,
          isFeatured: Boolean(p.isFeatured),
          specifications: p.specifications?.length
            ? p.specifications
            : emptyInitial.specifications,
          variantOptions: (p.variantOptions || []).map((o) => {
            const entries = normalizeOptionValues(o.values);
            return {
              name: o.name,
              valuesText: entries.map((e) => e.label).join(", "),
              colorValues: entries.map((e) => ({
                label: e.label,
                color: e.color || guessColorFromLabel(e.label),
              })),
            };
          }),
          variants: (p.variants || []).map((v) => ({
            sku: v.sku || "",
            price: v.price ?? "",
            stock: v.stock ?? 0,
            image: v.image || "",
            isDefault: Boolean(v.isDefault),
            attributes: v.attributes || {},
          })),
        });
      })
      .catch((err) => {
        toast.error(getErrorMessage(err, "Failed to load product"));
        navigate("/dashboard/products");
      })
      .finally(() => setLoadingProduct(false));
  }, [id, isEdit, navigate]);

  const categoryOptions = useMemo(
    () => getRootCategoryOptions(categories),
    [categories]
  );

  // Products saved before primary was limited to top level may point at a subcategory
  const primaryOptionsFor = (selectedId) => {
    if (!selectedId) return categoryOptions;
    if (categoryOptions.some((o) => String(o.id) === String(selectedId))) {
      return categoryOptions;
    }
    const legacy = flattenCategoryOptions(categories).find(
      (o) => String(o.id) === String(selectedId)
    );
    return legacy ? [...categoryOptions, legacy] : categoryOptions;
  };

  if (authLoading || loadingProduct) {
    return (
      <main className="admin-product-page">
        <FormPageSkeleton />
      </main>
    );
  }

  if (!isAuthenticated || user?.role !== "admin") {
    return (
      <Navigate to="/login" state={{ from: "/dashboard/products/new" }} replace />
    );
  }

  return (
    <main className="admin-product-page">
      <div className="admin-product-header">
        <div>
          <span className="admin-badge">Admin</span>
          <h1>{isEdit ? "Edit Product" : "Add Product"}</h1>
          <p>
            Create products with specifications, images, and dynamic options
            (color, size, model, etc.) like the SG Office catalog.
          </p>
        </div>
        <Button
          variant="outline-light"
          onClick={() => navigate("/dashboard/products")}
        >
          Back to list
        </Button>
      </div>

      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={adminProductSchema}
        onSubmit={async (values, { setSubmitting }) => {
          try {
            const images = await materializeProductImages(
              Array.isArray(values.images) ? values.images : [],
              pendingImageFilesRef.current
            );
            const payload = {
              name: values.name,
              sku: values.sku || undefined,
              price: Number(values.price),
              compareAtPrice:
                values.compareAtPrice === "" || values.compareAtPrice == null
                  ? null
                  : Number(values.compareAtPrice),
              stock: Number(values.stock) || 0,
              category: values.category,
              categories: values.categories?.length
                ? values.categories
                : [values.category],
              manufacturer: values.manufacturer,
              brand: values.brand,
              description: values.description,
              shortDescription: values.shortDescription,
              tags: values.tags
                .split(",")
                .map((t) => t.trim())
                .filter(Boolean),
              images,
              isActive: values.isActive,
              isFeatured: values.isFeatured,
              specifications: (values.specifications || []).filter(
                (s) => s.label && s.value
              ),
              variantOptions: (values.variantOptions || [])
                .map((o) => {
                  const entries = getOptionEntries(o);
                  if (!o.name?.trim() || !entries.length) return null;
                  return {
                    name: o.name.trim(),
                    values: entries,
                  };
                })
                .filter(Boolean),
              variants: (values.variants || []).map((v, idx) => ({
                sku: v.sku || "",
                attributes: v.attributes || {},
                price:
                  v.price === "" || v.price == null ? null : Number(v.price),
                stock: Number(v.stock) || 0,
                image: v.image || "",
                isDefault: Boolean(v.isDefault) || idx === 0,
              })),
            };

            if (isEdit) {
              await catalogApi.updateProduct(id, payload);
              toast.success("Product updated");
            } else {
              await catalogApi.createProduct(payload);
              toast.success("Product created");
            }
            await deleteRemovedProductImages(committedImagesRef.current, images);
            committedImagesRef.current = [...images];
            pendingImageFilesRef.current.clear();
            navigate("/dashboard/products");
          } catch (error) {
            toast.error(getErrorMessage(error, "Could not save product"));
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({
          values,
          setFieldValue,
          setFieldTouched,
          touched,
          errors,
          isSubmitting,
          handleChange,
        }) => (
          <FormikForm className="admin-product-form" noValidate>
            <Card className="admin-card">
              <Card.Body>
                <h3>Basic details</h3>
                <Row>
                  <Col md={8}>
                    <FormField name="name" label="Product name" placeholder="e.g. Durable Clip File Folder..." />
                  </Col>
                  <Col md={4}>
                    <FormField name="sku" label="SKU" placeholder="2200LB" />
                  </Col>
                </Row>
                <Row>
                  <Col md={4}>
                    <FormField name="price" type="number" label="Price (AED)" />
                  </Col>
                  <Col md={4}>
                    <FormField name="compareAtPrice" type="number" label="Compare at price" />
                  </Col>
                  <Col md={4}>
                    <FormField name="stock" type="number" label="Base stock" />
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <FormField name="manufacturer" label="Manufacturer" placeholder="Durable" />
                  </Col>
                  <Col md={6}>
                    <FormField name="brand" label="Brand" />
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <CategoryAutocomplete
                      label="Primary category"
                      options={primaryOptionsFor(values.category)}
                      value={values.category}
                      touched={touched.category}
                      error={errors.category}
                      onBlur={() => setFieldTouched("category", true)}
                      onChange={(id) => {
                        setFieldValue("category", id);
                        setFieldTouched("category", true, false);
                        // Keep only additional cats that still belong under the new primary
                        const allowed = new Set(
                          getSubtreeOptions(id, categories).map((o) => String(o.id))
                        );
                        const nextAdditional = (values.categories || []).filter((c) =>
                          allowed.has(String(c))
                        );
                        setFieldValue("categories", nextAdditional);
                      }}
                      placeholder="Type to search top-level categories..."
                      helpText={
                        <>
                          Top-level categories only.{" "}
                          <Link to="/dashboard/categories">Manage categories</Link>
                        </>
                      }
                    />
                  </Col>
                  <Col md={6}>
                    <CategoryMultiAutocomplete
                      label="Additional categories"
                      options={getSubtreeOptions(values.category, categories)}
                      value={values.categories || []}
                      onChange={(ids) => setFieldValue("categories", ids)}
                      disabled={!values.category}
                      emptyMessage={
                        values.category
                          ? "No subcategories under this primary category"
                          : "Select a primary category first"
                      }
                      placeholder="Type to search subcategories..."
                      helpText="Suggestions load from the selected primary category’s subcategories."
                    />
                  </Col>
                </Row>
                <FormField
                  name="shortDescription"
                  type="textarea"
                  rows={2}
                  label="Short description"
                />
                <FormField
                  name="description"
                  type="textarea"
                  rows={5}
                  label="Full description"
                />
                <FormField
                  name="tags"
                  label="Tags (comma separated)"
                  placeholder="FILES & FOLDERS, CLIP BOARD"
                />
                <ProductImageManager
                  images={values.images || []}
                  onChange={(next) => setFieldValue("images", next)}
                  pendingFilesRef={pendingImageFilesRef}
                />
                <div className="d-flex gap-4 mb-2">
                  <Form.Check
                    type="switch"
                    id="isActive"
                    label="Active"
                    checked={values.isActive}
                    onChange={(e) => setFieldValue("isActive", e.target.checked)}
                  />
                  <Form.Check
                    type="switch"
                    id="isFeatured"
                    label="Featured"
                    checked={values.isFeatured}
                    onChange={(e) => setFieldValue("isFeatured", e.target.checked)}
                  />
                </div>
              </Card.Body>
            </Card>

            <Card className="admin-card">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h3 className="mb-0">Specifications</h3>
                  <Badge bg="secondary">Optional</Badge>
                </div>
                <p className="admin-help">
                  Optional product facts shown as a list on the product page
                  (e.g. Material, Brand, Pack quantity). Not for customer choices.
                </p>
                <FieldArray name="specifications">
                  {({ push, remove }) => (
                    <>
                      {values.specifications.map((_, index) => (
                        <Row key={index} className="align-items-end">
                          <Col md={5}>
                            <FormField
                              name={`specifications.${index}.label`}
                              label="Label"
                              placeholder="Material"
                            />
                          </Col>
                          <Col md={5}>
                            <FormField
                              name={`specifications.${index}.value`}
                              label="Value"
                              placeholder="e.g. Plastic"
                            />
                          </Col>
                          <Col md={2} className="mb-3">
                            <Button
                              variant="outline-danger"
                              onClick={() => remove(index)}
                              type="button"
                            >
                              <FaTrash />
                            </Button>
                          </Col>
                        </Row>
                      ))}
                      <Button
                        type="button"
                        variant="outline-primary"
                        className="admin-add-btn"
                        onClick={() => push({ label: "", value: "" })}
                      >
                        <FaPlus /> Add specification
                      </Button>
                    </>
                  )}
                </FieldArray>
              </Card.Body>
            </Card>

            <Card className="admin-card">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h3 className="mb-0">Dynamic variants</h3>
                  <Badge bg="dark">Color, Size, Model…</Badge>
                </div>
                <p className="admin-help">
                  Add option types (e.g. Color, Size). For Color, pick a swatch
                  and name for each value. Then generate purchasable variants.
                </p>

                <FieldArray name="variantOptions">
                  {({ push, remove }) => (
                    <>
                      {values.variantOptions.map((option, index) => {
                        const isColor = isColorOptionName(option.name);
                        return (
                          <div key={index} className="admin-variant-option">
                            <Row className="align-items-end">
                              <Col md={4}>
                                <FormField
                                  name={`variantOptions.${index}.name`}
                                  label="Option name"
                                  placeholder="Color"
                                />
                              </Col>
                              {!isColor && (
                                <Col md={6}>
                                  <FormField
                                    name={`variantOptions.${index}.valuesText`}
                                    label="Values (comma separated)"
                                    placeholder="A4, A5, Letter"
                                  />
                                </Col>
                              )}
                              <Col md={isColor ? 8 : 2} className="mb-3">
                                <Button
                                  variant="outline-danger"
                                  type="button"
                                  onClick={() => remove(index)}
                                >
                                  <FaTrash />
                                </Button>
                              </Col>
                            </Row>

                            {isColor && (
                              <div className="admin-color-values">
                                <label className="form-label">Color values</label>
                                {(option.colorValues || []).map((cv, cIdx) => (
                                  <div
                                    key={cIdx}
                                    className="admin-color-row"
                                  >
                                    <input
                                      type="color"
                                      className="admin-color-picker"
                                      value={cv.color || "#888888"}
                                      onChange={(e) => {
                                        const next = [
                                          ...(option.colorValues || []),
                                        ];
                                        next[cIdx] = {
                                          ...next[cIdx],
                                          color: e.target.value,
                                        };
                                        setFieldValue(
                                          `variantOptions.${index}.colorValues`,
                                          next
                                        );
                                      }}
                                    />
                                    <Form.Control
                                      placeholder="e.g. Tundra Umber"
                                      value={cv.label || ""}
                                      onChange={(e) => {
                                        const next = [
                                          ...(option.colorValues || []),
                                        ];
                                        next[cIdx] = {
                                          ...next[cIdx],
                                          label: e.target.value,
                                        };
                                        setFieldValue(
                                          `variantOptions.${index}.colorValues`,
                                          next
                                        );
                                      }}
                                    />
                                    <span
                                      className="admin-color-preview-chip"
                                      title="Preview"
                                    >
                                      <span
                                        className="admin-color-swatch"
                                        style={{
                                          background:
                                            cv.color ||
                                            guessColorFromLabel(cv.label),
                                        }}
                                      />
                                      {cv.label || "Color name"}
                                    </span>
                                    <Button
                                      type="button"
                                      variant="outline-danger"
                                      size="sm"
                                      onClick={() => {
                                        const next = (
                                          option.colorValues || []
                                        ).filter((_, i) => i !== cIdx);
                                        setFieldValue(
                                          `variantOptions.${index}.colorValues`,
                                          next
                                        );
                                      }}
                                    >
                                      <FaTrash />
                                    </Button>
                                  </div>
                                ))}
                                <Button
                                  type="button"
                                  variant="outline-primary"
                                  size="sm"
                                  className="admin-add-btn"
                                  onClick={() =>
                                    setFieldValue(
                                      `variantOptions.${index}.colorValues`,
                                      [
                                        ...(option.colorValues || []),
                                        {
                                          label: "",
                                          color: "#888888",
                                        },
                                      ]
                                    )
                                  }
                                >
                                  <FaPlus /> Add color
                                </Button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                      <div className="d-flex gap-2 flex-wrap mb-3">
                        <Button
                          type="button"
                          variant="outline-primary"
                          className="admin-add-btn"
                          onClick={() =>
                            push({
                              name: "",
                              valuesText: "",
                              colorValues: [
                                { label: "", color: "#888888" },
                              ],
                            })
                          }
                        >
                          <FaPlus /> Add option type
                        </Button>
                        <Button
                          type="button"
                          variant="outline-dark"
                          className="admin-add-btn"
                          onClick={() =>
                            push({
                              name: "Color",
                              valuesText: "",
                              colorValues: [
                                {
                                  label: "Tundra Umber",
                                  color: "#2f2c2a",
                                },
                                {
                                  label: "Canyon Orange",
                                  color: "#e8a06e",
                                },
                              ],
                            })
                          }
                        >
                          <FaPlus /> Add Color option
                        </Button>
                        <Button
                          type="button"
                          variant="warning"
                          onClick={() => {
                            const next = buildVariantsFromOptions(
                              values.variantOptions,
                              values.variants
                            );
                            setFieldValue("variants", next);
                            toast.info(
                              next.length
                                ? `Generated ${next.length} variants`
                                : "Add option values first"
                            );
                          }}
                        >
                          Generate variants
                        </Button>
                      </div>
                    </>
                  )}
                </FieldArray>

                {values.variants?.length > 0 && (
                  <div className="table-responsive">
                    <Table bordered hover className="admin-variant-table">
                      <thead>
                        <tr>
                          <th>Options</th>
                          <th>SKU</th>
                          <th>Price</th>
                          <th>Stock</th>
                          <th>Image URL</th>
                          <th>Default</th>
                        </tr>
                      </thead>
                      <tbody>
                        {values.variants.map((variant, index) => (
                          <tr key={index}>
                            <td>
                              {Object.entries(variant.attributes || {}).map(
                                ([k, v]) => (
                                  <Badge key={k} bg="secondary" className="me-1">
                                    {k}: {v}
                                  </Badge>
                                )
                              )}
                            </td>
                            <td>
                              <Form.Control
                                value={variant.sku}
                                onChange={(e) =>
                                  setFieldValue(`variants.${index}.sku`, e.target.value)
                                }
                              />
                            </td>
                            <td>
                              <Form.Control
                                type="number"
                                value={variant.price}
                                placeholder="Base"
                                onChange={(e) =>
                                  setFieldValue(
                                    `variants.${index}.price`,
                                    e.target.value
                                  )
                                }
                              />
                            </td>
                            <td>
                              <Form.Control
                                type="number"
                                value={variant.stock}
                                onChange={(e) =>
                                  setFieldValue(
                                    `variants.${index}.stock`,
                                    e.target.value
                                  )
                                }
                              />
                            </td>
                            <td>
                              <Form.Control
                                value={variant.image}
                                onChange={(e) =>
                                  setFieldValue(
                                    `variants.${index}.image`,
                                    e.target.value
                                  )
                                }
                              />
                            </td>
                            <td className="text-center">
                              <Form.Check
                                type="radio"
                                name="defaultVariant"
                                checked={Boolean(variant.isDefault)}
                                onChange={() => {
                                  const next = values.variants.map((v, i) => ({
                                    ...v,
                                    isDefault: i === index,
                                  }));
                                  setFieldValue("variants", next);
                                }}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </Card.Body>
            </Card>

            <div className="admin-submit-row">
              <Button type="submit" className="admin-save-btn" disabled={isSubmitting}>
                <FaSave /> {isSubmitting ? "Saving..." : "Save product"}
              </Button>
            </div>
          </FormikForm>
        )}
      </Formik>
    </main>
  );
};

export default AdminProductForm;
